import { useEffect, useMemo, useRef, useState } from 'react';
import { includes, isEmpty, reduce } from 'lodash';

import { DataTable, DataTableColumn } from '@xtnd/web/shared/components';
import { numberUtil, dateUtil, cn } from '@xtnd/web/shared/utils';
import { AppealStatus, AppealType } from '@repo/types';
import { ActionsMenuItem, ActionsMenu, Button, Checkbox, Icon, showToast } from '@repo/ui';

import { ArAppealStatus, useMatterARContext } from '../common';
import { Appeal, MatterInvoice, UpdateAppealStatusPayload } from '../../types';
import {
  useCreateCutsAppliedWriteOff,
  useMarkPartialWriteOff,
  useMatterInvoicesQuery,
  useUpdateMatterInvoiceCarrierInPreview,
  useRollbackInvoice,
} from '../../queries';
import { MarkToBeAppealedModal } from './MarkToBeAppealed';
import { MarkWriteOffRemainingModal } from '../appeals/MarkWriteOffRemainingModal';
import { DAYS_PAST_DUE } from '../../constants';
import { MarkNoAppealModal } from './MarkNoAppeal';
import BARColumn from '../common/BARColumn';
import { InvoiceActivity } from './InvoiceActivity';
import MarkPartialWriteOffModal from '../common/MarkPartialWriteOff';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { getApiErrorMessage } from '../../utils/apiErrorHandler';

type Props = {
  onOpenInvoiceDetail: (invoice: MatterInvoice) => void;
};

export default function InvoiceList({ onOpenInvoiceDetail }: Props) {
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const { currentMatterId, currentClientId, daysPastDue, daysPastDueCap } = useMatterARContext();
  const { data, isLoading } = useMatterInvoicesQuery(
    currentMatterId,
    currentClientId,
    daysPastDue,
    daysPastDueCap
  );
  const updateMutation = useUpdateMatterInvoiceCarrierInPreview();
  const { mutateAsync: rollbackInvoice } = useRollbackInvoice();
  const [mutatingRows, setMutatingRows] = useState<Set<string>>(new Set());
  const [actionSelectedInvoice, setActionSelectedInvoice] = useState<MatterInvoice>(
    {} as MatterInvoice
  );
  const [actionRef, setActionRef] = useState<HTMLButtonElement | null>(null);
  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  const [isMarkToBeAppealModalOpen, setIsMarkToBeAppealModalOpen] = useState(false);
  const [isMarkAppealSubmittedModalOpen, setIsMarkAppealSubmittedModalOpen] = useState(false);
  const [isResolutionModalOpen, setResolutionModalOpen] = useState(false);
  const [isOpenBARModal, setIsOpenBARModal] = useState(false);

  const [isMarkDiscountOnlyModalOpen, setIsMarkDiscountOnlyModalOpen] = useState(false);
  const [isMarkDiscountOnlyLoading, setIsMarkDiscountOnlyLoading] = useState(false);

  const { mutateAsync: markPartialWriteOffMutation } = useMarkPartialWriteOff();

  // Activity drawer state
  const [isActivityOpen, setIsActivityOpen] = useState(false);
  const [selectedActivityInvoice, setSelectedActivityInvoice] = useState<MatterInvoice | null>(
    null
  );

  // Rollback confirmation dialog state
  const [isRollbackDialogOpen, setIsRollbackDialogOpen] = useState(false);
  const [invoiceToRollback, setInvoiceToRollback] = useState<MatterInvoice | null>(null);
  const [isRollbackLoading, setIsRollbackLoading] = useState(false);

  // Write-off remaining modal state
  const [writeOffRemainingAppeal, setWriteOffRemainingAppeal] = useState<Appeal | null>(null);
  const [isWriteOffRemainingLoading, setIsWriteOffRemainingLoading] = useState(false);
  const { mutateAsync: createCutsAppliedWriteOff } = useCreateCutsAppliedWriteOff();

  const mastersInvoicesColumns: DataTableColumn<MatterInvoice>[] = [
    {
      key: 'invoiceNumber',
      header: 'Invoice #',
      align: 'left',
      renderCell: row => (
        <div className="text-xtnd-dark dark:text-xtnd-light">
          <div className="">{`${row.invoiceNumber} ${row.isSplitInvoice ? '(Split Invoice)' : ''}`}</div>
        </div>
      ),
    },
    {
      key: 'invoiceDate',
      header: 'Date',
      align: 'left',
      renderCell: row => (
        <div className="text-xtnd-dark dark:text-xtnd-light">
          <div className="">
            {dateUtil.formatDate(row.invoiceDate, dateUtil.DateFormat.US)}
          </div>
        </div>
      ),
    },
    {
      key: 'amountPaid',
      header: 'Invoice Amount',
      align: 'left',
      className: 'w-[3rem]',
      renderCell: row => {
        return (
          <div className="flex flex-col">
            <span className="text-[10px] text-gray-500 uppercase tracking-wide">
              Invoice Amount
            </span>
            <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {numberUtil.formatCurrency(row.total)}
            </span>

            <div className="flex gap-2 text-xs mt-1 justify-between">
              <span className="text-gray-600 dark:text-gray-400">Due</span>
              <span className="text-green-600 font-medium">
                {numberUtil.formatCurrency(row.outstandingBalance)}
              </span>
            </div>

            <div className="flex gap-2 text-xs justify-between">
              <span className="text-gray-600 dark:text-gray-400">Paid</span>
              <span className="text-gray-900 dark:text-gray-100">
                {numberUtil.formatCurrency(row.paid ?? 0)}
              </span>
            </div>

            <div className="flex gap-2 text-xs justify-between">
              <span className="text-gray-600 dark:text-gray-400">Credits</span>
              <span className="text-gray-900 dark:text-gray-100">
                {numberUtil.formatCurrency(row.creditsIssued ?? 0)}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      key: 'outstandingBalance',
      header: 'Outstanding AR',
      align: 'left',
      renderCell: row => {
        return (
          <div className="flex flex-col items-start">
            <div className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 mt-1 w-full flex justify-between items-center">
              <span className="text-[10px] text-gray-900 dark:text-gray-100 uppercase tracking-wide block">
                Outstanding Balance
              </span>
              <span className="text-green-600 font-semibold text-sm">
                {numberUtil.formatCurrency(row.arBalance ?? 0)}
              </span>
            </div>
            {/* TODO: Use the writeOffAmount after backend sends that field */}

            <div className="flex gap-2 text-xs mt-1 justify-between">
              <span className="text-gray-600 dark:text-gray-400">Total Agreed to Pay:</span>
              <span className="text-gray-900 dark:text-gray-100">
                {numberUtil.formatCurrency(row.collectedAmount ?? 0)}
              </span>
            </div>
            <div className="flex text-[10px] mt-1 w-full justify-between">
              {/* <div className="flex gap-1">
                  <span className="text-gray-600 dark:text-gray-400">W/O:</span>
                  <span className="text-gray-900 dark:text-gray-100">
                    {numberUtil.formatCurrency(row.writeOffAmount ?? 0)}
                  </span>
                </div> */}
              {/* <div className="flex gap-1">
                  <span className="text-gray-600 dark:text-gray-400">T/B Appealed:</span>
                  <span className="text-gray-900 dark:text-gray-100 text">
                    {numberUtil.formatCurrency(row.remainingAmount ?? 0)}
                  </span>
                </div> */}
            </div>
          </div>
        );
      },
    },
    {
      key: '',
      header: 'BAR',
      align: 'center',
      renderCell: row => (
        <BARColumn
          invoiceId={row.invoiceId}
          onListenOpenEvent={isOpen => setIsOpenBARModal(isOpen)}
          key={`matter-invoice-bar-column-${row.invoiceId}`}
        />
      ),
    },
    {
      key: 'daysPastDue',
      header: 'Days Past Due',
      align: 'right',
      renderCell: row => (
        <div className="text-xtnd-dark dark:text-xtnd-light">
          <div
            className={`inline-block w-fit text-xtnd-dark dark:text-xtnd-light px-2 py-1 rounded-full ${row.daysPastDue > DAYS_PAST_DUE ? 'bg-[#ef4444] text-xtnd-white' : 'dark:bg-xtnd-dark bg-xtnd-light'}`}
          >
            {row.daysPastDue} days
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Appeal Status',
      align: 'left',
      renderCell: row => <ArAppealStatus status={row.appealStatus} />,
    },
    {
      key: 'isCarrierInPreview',
      header: 'In Review',
      align: 'left',
      renderCell: row => {
        if (updateMutation.isPending && selectedInvoiceId && selectedInvoiceId === row.invoiceId) {
          return <Icon className="animate-spin" name="FaSpinner" size="xs" />;
        }

        return (
          <Checkbox
            checked={invoicesInPreview[row.invoiceId] || false}
            disabled={updateMutation.isPending}
            onClick={e => e.stopPropagation()}
            onChange={e => {
              const checked = e.target.checked;
              setSelectedInvoiceId(row.invoiceId);
              updateMutation.mutate({ invoiceId: row.invoiceId, isCarrierInPreview: checked });
            }}
          />
        );
      },
    },
    {
      key: 'action',
      header: 'Actions',
      align: 'center',
      renderCell: row => {
        const showStatusActions = includes([AppealStatus.PRE_APPEAL], row.appealStatus);
        const isMutating = mutatingRows.has(row.invoiceId);

        return (
          <div className="relative items-center justify-center">
            <Button
              variant="ghost"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                setActionRef(e.currentTarget);
                setActionSelectedInvoice(row);
              }}
              className={cn(
                actionSelectedInvoice?.invoiceId === row.invoiceId
                  ? 'bg-xtnd-light dark:bg-xtnd-dark '
                  : '',
                'outline-none'
              )}
              title="More actions"
            >
              <Icon name="HiDotsVertical" className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </Button>

            <ActionsMenu
              isOpen={actionSelectedInvoice?.invoiceId === row.invoiceId}
              onClose={() => {
                setActionSelectedInvoice({} as MatterInvoice);
                setActionRef(null);
              }}
              ref={actionMenuRef}
              triggerElement={actionRef}
            >
              {showStatusActions && (
                <>
                  <ActionsMenuItem
                    icon="HiOutlineDocument"
                    iconColor="text-xtnd-blue"
                    label="To Be Appealed"
                    onClick={() => {
                      if (isEmpty(row.appealId)) {
                        showToast.error(
                          'The invoice is missing "Appeal ID". Please refresh the page and try again.'
                        );
                        return;
                      }
                      setIsMarkToBeAppealModalOpen(true);
                    }}
                  />
                  <ActionsMenuItem
                    icon="HiOutlineClipboardCheck"
                    iconColor="text-xtnd-blue"
                    label="Appeal Submitted (Backlog)"
                    onClick={() => {
                      if (isEmpty(row.appealId)) {
                        showToast.error(
                          'The invoice is missing "Appeal ID". Please refresh the page and try again.'
                        );
                        return;
                      }
                      setIsMarkAppealSubmittedModalOpen(true);
                    }}
                  />
                  <ActionsMenuItem
                    icon="HiOutlineCash"
                    key={`mark-discount-only-${row.invoiceId}`}
                    iconColor="text-xtnd-blue"
                    label="No Appeal"
                    onClick={() => {
                      if (isEmpty(row.appealId)) {
                        showToast.error(
                          'The invoice is missing "Appeal ID". Please refresh the page and try again.'
                        );
                        return;
                      }

                      setIsMarkDiscountOnlyModalOpen(true);
                    }}
                  />
                </>
              )}
              {[AppealStatus.APPEAL_DENIED, AppealStatus.DEADLINE_EXPIRED].includes(row.appealStatus as AppealStatus) && (
                <ActionsMenuItem
                  key={`write-off-remaining-${row.invoiceId}`}
                  icon="HiCurrencyDollar"
                  iconColor="text-red-500"
                  label="Write-Off Remaining Amount"
                  disabled={isMutating}
                  onClick={e => {
                    e.stopPropagation();
                    setWriteOffRemainingAppeal({
                      invoiceId: row.invoiceId,
                      appealId: row.appealId,
                      invoiceNumber: row.invoiceNumber,
                      matterDisplayNumber: row.matterDisplayNumber ?? '',
                      clientName: row.clientName,
                      invoiceDate: row.invoiceDate ?? '',
                      paid: row.paid ?? 0,
                      totalAmount: row.total,
                      creditsIssued: row.creditsIssued ?? 0,
                      responsibleAttorneyName: row.responsibleAttorneyName ?? '',
                    } as Appeal);
                    setActionSelectedInvoice({} as MatterInvoice);
                    setActionRef(null);
                  }}
                />
              )}
              <ActionsMenuItem
                key="activity"
                icon="HiClock"
                iconColor="text-xtnd-blue"
                label="Activity"
                onClick={() => {
                  setSelectedActivityInvoice(row);
                  setIsActivityOpen(true);
                  setActionSelectedInvoice({} as MatterInvoice);
                  setActionRef(null);
                }}
              />
              {row.appealStatus === AppealStatus.APPEAL_SUBMITTED && (
                <ActionsMenuItem
                  key={`rollback-${row.invoiceId}`}
                  icon="HiRewind"
                  iconColor="text-amber-500"
                  label="Rollback to Pre-Appeal"
                  disabled={isMutating}
                  onClick={e => {
                    e.stopPropagation();
                    handleRollbackClick(row);
                    setActionSelectedInvoice({} as MatterInvoice);
                    setActionRef(null);
                  }}
                />
              )}
            </ActionsMenu>
          </div>
        );
      },
    },
  ];

  const matterInvoices = useMemo(() => (data ? (data.data as MatterInvoice[]) : []), [data]);

  const invoicesInPreview = useMemo(() => {
    const invoicesInPreview = reduce(
      matterInvoices,
      (acc: Record<string, boolean>, inv) => ({ ...acc, [inv.invoiceId]: inv.isCarrierInPreview }),
      {} as Record<string, boolean>
    );

    if (updateMutation.data) {
      const updatedInvoice = updateMutation.data as any as MatterInvoice;
      invoicesInPreview[updatedInvoice.invoiceId] = updatedInvoice.isCarrierInPreview;
    }

    return invoicesInPreview;
  }, [matterInvoices, updateMutation.data]);

  const handleMutationStateChange = (invoiceId: string, isPending: boolean) => {
    setMutatingRows(prev => {
      const next = new Set(prev);
      if (isPending) {
        next.add(invoiceId);
      } else {
        next.delete(invoiceId);
      }
      return next;
    });
  };

  const handleRollbackClick = (invoice: MatterInvoice) => {
    setInvoiceToRollback(invoice);
    setIsRollbackDialogOpen(true);
  };

  const handleRollbackConfirm = async () => {
    if (!invoiceToRollback) return;

    try {
      setIsRollbackLoading(true);
      handleMutationStateChange(invoiceToRollback.invoiceId, true);
      await rollbackInvoice(invoiceToRollback.invoiceId);
    } finally {
      setIsRollbackLoading(false);
      handleMutationStateChange(invoiceToRollback.invoiceId, false);
      setIsRollbackDialogOpen(false);
      setInvoiceToRollback(null);
    }
  };

  const handleRollbackCancel = () => {
    if (isRollbackLoading) return;
    setIsRollbackDialogOpen(false);
    setInvoiceToRollback(null);
  };

  const handleWriteOffRemainingSubmit = async (feesCut: number, expensesCut: number, comment: string, appealId: string) => {
    if (!writeOffRemainingAppeal) return;
    setIsWriteOffRemainingLoading(true);
    try {
      await createCutsAppliedWriteOff({
        invoiceId: writeOffRemainingAppeal.invoiceId,
        appealId,
        feesCut,
        expensesCut,
        comment,
      });
      showToast.success('Write-off created successfully.');
      setWriteOffRemainingAppeal(null);
    } catch (error) {
      showToast.error(getApiErrorMessage(error, 'Failed to create write-off. Please try again.'));
    } finally {
      setIsWriteOffRemainingLoading(false);
    }
  };

  const getRowClassName = (row: MatterInvoice) => {
    const baseClass = 'hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150';
    if (mutatingRows.has(row.invoiceId)) {
      return `${baseClass} bg-gray-50 dark:bg-gray-700 opacity-60 pointer-events-none cursor-not-allowed`;
    }
    return baseClass;
  };

  useEffect(() => {
    if (!updateMutation.isPending && selectedInvoiceId) {
      setSelectedInvoiceId(null);
    }
  }, [updateMutation.isPending, selectedInvoiceId]);

  useEffect(() => {
    if (
      isEmpty(actionSelectedInvoice) ||
      isMarkToBeAppealModalOpen ||
      isMarkAppealSubmittedModalOpen ||
      isResolutionModalOpen ||
      isMarkDiscountOnlyModalOpen ||
      !!writeOffRemainingAppeal
    ) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (actionRef?.contains(target)) {
        return;
      }

      if (actionMenuRef.current?.contains(target)) {
        return;
      }

      setActionSelectedInvoice({} as MatterInvoice);
      setActionRef(null);
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [
    actionSelectedInvoice,
    actionRef,
    isMarkToBeAppealModalOpen,
    isMarkAppealSubmittedModalOpen,
    isResolutionModalOpen,
    isMarkDiscountOnlyModalOpen,
    writeOffRemainingAppeal,
  ]);

  const handleRowSelected = (row: MatterInvoice) => {
    if (!isEmpty(actionSelectedInvoice) || isOpenBARModal) {
      return;
    }

    onOpenInvoiceDetail(row);
  };

  const handleSubmitMarkDiscountOnly = async (payload: UpdateAppealStatusPayload) => {
    setIsMarkDiscountOnlyLoading(true);
    try {
      await markPartialWriteOffMutation(payload);
      setActionSelectedInvoice({} as MatterInvoice);
    } finally {
      setIsMarkDiscountOnlyLoading(false);
    }
  };

  return (
    <div>
      <DataTable
        columns={mastersInvoicesColumns}
        isLoading={isLoading}
        onRowClick={row => handleRowSelected(row)}
        getRowClassName={getRowClassName}
        data={{
          data: matterInvoices,
          total: matterInvoices.length,
        }}
        emptyState={{
          icon: 'HiClipboardList',
          title: 'No outstanding invoices available',
          description: 'There are no outstanding invoices found.',
        }}
      />
      <MarkToBeAppealedModal
        isOpen={isMarkToBeAppealModalOpen}
        invoiceId={actionSelectedInvoice?.invoiceId ?? ''}
        onClose={() => {
          setIsMarkToBeAppealModalOpen(false);
          setActionSelectedInvoice({} as MatterInvoice);
        }}
      />

      <MarkToBeAppealedModal
        isOpen={isMarkAppealSubmittedModalOpen}
        invoiceId={actionSelectedInvoice?.invoiceId ?? ''}
        initialAppealType={AppealType.BACKLOG}
        onClose={() => {
          setIsMarkAppealSubmittedModalOpen(false);
          setActionSelectedInvoice({} as MatterInvoice);
        }}
      />

      <MarkNoAppealModal
        isOpen={isResolutionModalOpen}
        invoiceId={actionSelectedInvoice?.invoiceId ?? ''}
        invoiceNumber={actionSelectedInvoice?.invoiceNumber ?? ''}
        onClose={() => {
          setResolutionModalOpen(false);
          setActionSelectedInvoice({} as MatterInvoice);
        }}
        basicInformation={{
          matterDisplayNumber: actionSelectedInvoice?.matterDisplayNumber ?? '',
          clientName: actionSelectedInvoice?.clientName ?? '',
          invoiceDate: actionSelectedInvoice?.invoiceDate ?? '',
          invoiceNumber: actionSelectedInvoice?.invoiceNumber ?? '',
          totalAmount: actionSelectedInvoice?.total ?? 0,
          amountDue: actionSelectedInvoice?.outstandingBalance ?? 0,
          paid: actionSelectedInvoice?.paid ?? 0,
          creditsIssued: actionSelectedInvoice?.creditsIssued ?? 0,
          responsibleAttorneyName: actionSelectedInvoice?.responsibleAttorneyName ?? '',
        }}
      />
      {actionSelectedInvoice?.invoiceId && (
        <MarkPartialWriteOffModal
          isOpen={isMarkDiscountOnlyModalOpen}
          invoiceId={actionSelectedInvoice?.invoiceId ?? ''}
          invoiceNumber={actionSelectedInvoice?.invoiceNumber ?? ''}
          isLoading={isMarkDiscountOnlyLoading}
          setLoading={loading => setIsMarkDiscountOnlyLoading(loading.discountOnly)}
          onSubmit={payload => handleSubmitMarkDiscountOnly(payload)}
          onClose={() => {
            setIsMarkDiscountOnlyModalOpen(false);
            setActionSelectedInvoice(null as unknown as MatterInvoice);
          }}
        />
      )}

      {/* Activity Drawer */}
      <InvoiceActivity
        isOpen={isActivityOpen}
        onClose={() => {
          setIsActivityOpen(false);
          setSelectedActivityInvoice(null);
        }}
        invoiceNumber={selectedActivityInvoice?.invoiceNumber ?? ''}
        invoiceId={selectedActivityInvoice?.invoiceId ?? ''}
      />

      {/* Rollback Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isRollbackDialogOpen}
        title="Confirm Rollback to Pre-Appeal"
        message="Are you sure you want to rollback this invoice to Pre-Appeal? This action cannot be reversed."
        confirmText="Confirm"
        cancelText="Cancel"
        variant="destructive"
        isLoading={isRollbackLoading}
        onConfirm={handleRollbackConfirm}
        onCancel={handleRollbackCancel}
      />

      {/* Write-Off Remaining Amount Modal */}
      <MarkWriteOffRemainingModal
        isOpen={!!writeOffRemainingAppeal}
        isLoading={isWriteOffRemainingLoading}
        appeal={writeOffRemainingAppeal}
        onClose={() => setWriteOffRemainingAppeal(null)}
        onSubmit={handleWriteOffRemainingSubmit}
      />
    </div>
  );
}
